import React from "react";
import styled from "styled-components";

/**
 * ConsentEditor
 *
 * Props:
 *  - blocks: array of objects
 *       e.g. [
 *         { type: "text", content: "Please read and agree..." },
 *         { type: "checkbox", label: "I agree", required: true }
 *       ]
 *  - onChange: function(updatedBlocks) => void
 */
export default function ConsentEditor({ blocks, onChange }) {
  // Helper: update a single block at index
  const handleBlockChange = (index, updated) => {
    const newBlocks = [...blocks];
    newBlocks[index] = { ...newBlocks[index], ...updated };
    onChange(newBlocks);
  };

  const handleAddTextBlock = () => {
    onChange([...blocks, { type: "text", content: "New text" }]);
  };

  const handleAddCheckboxBlock = () => {
    onChange([
      ...blocks,
      { type: "checkbox", label: "New checkbox label", required: false },
    ]);
  };

  const handleDeleteBlock = (index) => {
    const newBlocks = [...blocks];
    newBlocks.splice(index, 1);
    onChange(newBlocks);
  };

  const moveBlockUp = (index) => {
    if (index === 0) return;
    const newBlocks = [...blocks];
    const temp = newBlocks[index];
    newBlocks[index] = newBlocks[index - 1];
    newBlocks[index - 1] = temp;
    onChange(newBlocks);
  };

  const moveBlockDown = (index) => {
    if (index === blocks.length - 1) return;
    const newBlocks = [...blocks];
    const temp = newBlocks[index];
    newBlocks[index] = newBlocks[index + 1];
    newBlocks[index + 1] = temp;
    onChange(newBlocks);
  };

  return (
    <EditorContainer>
      <EditorHeader>Consent Blocks</EditorHeader>

      <BlockList>
        {blocks.map((block, i) => {
          if (block.type === "text") {
            return (
              <BlockCard key={i}>
                <BlockCardHeader>
                  <BlockTitle>Text Block</BlockTitle>
                  <BlockButtons>
                    <IconButton onClick={() => moveBlockUp(i)} title="Move Up">
                      ▲
                    </IconButton>
                    <IconButton
                      onClick={() => moveBlockDown(i)}
                      title="Move Down"
                    >
                      ▼
                    </IconButton>
                    <DeleteButton
                      onClick={() => handleDeleteBlock(i)}
                      title="Delete Block"
                    >
                      ✕
                    </DeleteButton>
                  </BlockButtons>
                </BlockCardHeader>

                <Label>Content</Label>
                <StyledTextArea
                  rows={3}
                  value={block.content || ""}
                  onChange={(e) => handleBlockChange(i, { content: e.target.value })}
                  placeholder="Enter the text you want to display..."
                />
              </BlockCard>
            );
          } else if (block.type === "checkbox") {
            return (
              <BlockCard key={i}>
                <BlockCardHeader>
                  <BlockTitle>Checkbox Block</BlockTitle>
                  <BlockButtons>
                    <IconButton onClick={() => moveBlockUp(i)} title="Move Up">
                      ▲
                    </IconButton>
                    <IconButton
                      onClick={() => moveBlockDown(i)}
                      title="Move Down"
                    >
                      ▼
                    </IconButton>
                    <DeleteButton
                      onClick={() => handleDeleteBlock(i)}
                      title="Delete Block"
                    >
                      ✕
                    </DeleteButton>
                  </BlockButtons>
                </BlockCardHeader>

                <Label>Label</Label>
                <StyledInput
                  type="text"
                  value={block.label || ""}
                  onChange={(e) => handleBlockChange(i, { label: e.target.value })}
                  placeholder="Enter the checkbox label..."
                />

                <CheckboxRow>
                  <CheckboxLabel>Required?</CheckboxLabel>
                  <StyledCheckbox
                    type="checkbox"
                    checked={block.required || false}
                    onChange={(e) => handleBlockChange(i, { required: e.target.checked })}
                  />
                </CheckboxRow>
              </BlockCard>
            );
          }

          return (
            <BlockCard key={i}>
              <BlockCardHeader>
                <BlockTitle>Unknown block type</BlockTitle>
                <DeleteButton onClick={() => handleDeleteBlock(i)} title="Delete Block">
                  ✕
                </DeleteButton>
              </BlockCardHeader>
            </BlockCard>
          );
        })}
      </BlockList>

      <AddButtonRow>
        <PrimaryButton onClick={handleAddTextBlock}>
          + Add Text Block
        </PrimaryButton>
        <PrimaryButton onClick={handleAddCheckboxBlock}>
          + Add Checkbox Block
        </PrimaryButton>
      </AddButtonRow>
    </EditorContainer>
  );
}

/* ----------------------------- Styled Components ----------------------------- */

const EditorContainer = styled.div`
  max-width: 600px;
  margin: 2rem auto;
  padding: 1.5rem;
  background: #fafafa;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
`;

const EditorHeader = styled.h3`
  margin: 0 0 1rem;
  font-size: 1.5rem;
  color: #333;
  text-align: center;
`;

const BlockList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const BlockCard = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 1rem;
  border: 1px solid #dedede;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const BlockCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
`;

const BlockTitle = styled.span`
  font-weight: 600;
  font-size: 1rem;
`;

const BlockButtons = styled.div`
  display: flex;
  gap: 0.25rem;
`;

const IconButton = styled.button`
  background: #eef1f4;
  color: #333;
  border: none;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;

  &:hover {
    background: #dee1e4;
  }
`;

const DeleteButton = styled(IconButton)`
  background: #fce8e8;
  color: #c00;

  &:hover {
    background: #f8d1d1;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.25rem;
  font-weight: 500;
  color: #333;
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  font-size: 0.95rem;
  padding: 0.5rem;
  border: 1px solid #cfcfcf;
  border-radius: 4px;
  resize: vertical;
`;

const StyledInput = styled.input`
  display: block;
  width: 100%;
  font-size: 0.95rem;
  padding: 0.5rem;
  margin-bottom: 0.5rem;
  border: 1px solid #cfcfcf;
  border-radius: 4px;
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CheckboxLabel = styled.span`
  font-size: 0.95rem;
  color: #333;
`;

const StyledCheckbox = styled.input`
  cursor: pointer;
`;

const AddButtonRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  justify-content: center;
`;

const PrimaryButton = styled.button`
  background: #007bff;
  color: white;
  font-size: 0.95rem;
  padding: 0.6rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  min-width: 150px;

  &:hover {
    background: #0069d9;
  }
`;
